import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { camelize } from 'casing'
import { API, crmServiceUrl, errorLogger } from '@labrav/common'
import { type ResaleAuthorizationsResellerAgreements } from '../../../admin/modules/resaleAuthorizations/reducer'

const fetchResaleAuthorizationsListAPI = (
  partnerId: string,
  opportunityId: string
) =>
  API.get(
    `${crmServiceUrl}/partners/${partnerId}/reseller_authorizations`,
    {
      params: {
        crm_opportunity_id: opportunityId,
        sort_by: 'created_at',
        sort_order: 'DESC',
      },
    }
  )

async function fetchResaleAuthForHub(
  partnerId: string,
  oppId: string,
  setResellerAuthorizations: Dispatch<
    SetStateAction<ResaleAuthorizationsResellerAgreements[]>
  >,
  setResellerAuthorizationsError: Dispatch<SetStateAction<string | null>>,
  isCurrent: () => boolean
): Promise<void> {
  setResellerAuthorizationsError(null)
  try {
    const res = await fetchResaleAuthorizationsListAPI(partnerId, oppId)
    if (!isCurrent()) return
    setResellerAuthorizations(
      Array.isArray(res.data.reseller_authorizations)
        ? camelize(res.data.reseller_authorizations)
        : []
    )
  } catch (error) {
    if (!isCurrent()) return
    setResellerAuthorizations([])
    setResellerAuthorizationsError(
      'Unable to load reseller authorizations. Please try again.'
    )
    errorLogger()(error as Error)
  }
}

export const useResellerAuthData = ({
  partnerId,
  opportunityId,
  authLoading,
  canViewMarketplace,
}: {
  partnerId?: string
  opportunityId: string
  authLoading: boolean
  canViewMarketplace: boolean
}) => {
  const [resellerAuthorizations, setResellerAuthorizations] = useState<
    ResaleAuthorizationsResellerAgreements[]
  >([])
  const [resellerAuthorizationsError, setResellerAuthorizationsError] =
    useState<string | null>(null)

  const resellerAuthRequestRef = useRef(0)
  const fetchResellerAuth = useCallback(
    async (opportunityIdOverride?: string) => {
      const oppId = opportunityIdOverride ?? opportunityId
      if (!partnerId?.trim() || authLoading || !canViewMarketplace) return

      const requestId = ++resellerAuthRequestRef.current
      const isCurrent = () => resellerAuthRequestRef.current === requestId
      await fetchResaleAuthForHub(
        partnerId,
        oppId,
        setResellerAuthorizations,
        setResellerAuthorizationsError,
        isCurrent
      )
    },
    [partnerId, opportunityId, authLoading, canViewMarketplace]
  )

  useEffect(() => {
    if (
      !opportunityId ||
      !partnerId?.trim() ||
      authLoading ||
      !canViewMarketplace
    ) {
      return
    }
    void fetchResellerAuth(opportunityId)
    return () => {
      resellerAuthRequestRef.current += 1
    }
  }, [
    opportunityId,
    partnerId,
    authLoading,
    canViewMarketplace,
    fetchResellerAuth,
  ])

  return {
    resellerAuthorizations,
    resellerAuthorizationsError,
    setResellerAuthorizations,
    fetchResellerAuth,
  }
}
